// src/components/HomePage/SpotlightSlider.js

import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import { getAllBanners } from "../../services/HomePage/BannerApi";
import { FaChevronLeft, FaChevronRight, FaPlay, FaPause } from "react-icons/fa";

// ✅ Slick Carousel ki CSS
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// ✅ API ka URL
const API_BASE_URL = process.env.REACT_APP_API_URL;
const BANNER_IMG_URL = API_BASE_URL?.replace('/api/', '/BannerImage/');

// ✅ Left Arrow
const PrevArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute left-4 top-1/2 -translate-y-1/2 z-20 
               w-10 h-10 md:w-12 md:h-12 
               bg-white/90 backdrop-blur-md shadow-xl 
               rounded-full flex items-center justify-center
               transition-all duration-300
               border border-white/50
               hover:bg-white hover:scale-110
               focus:outline-none focus:ring-2 focus:ring-red-500"
    aria-label="Previous slide"
  >
    <FaChevronLeft className="text-red-600 text-sm md:text-base font-bold" />
  </button>
);

// ✅ Right Arrow
const NextArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute right-4 top-1/2 -translate-y-1/2 z-20 
               w-10 h-10 md:w-12 md:h-12 
               bg-white/90 backdrop-blur-md shadow-xl 
               rounded-full flex items-center justify-center
               transition-all duration-300
               border border-white/50
               hover:bg-white hover:scale-110
               focus:outline-none focus:ring-2 focus:ring-red-500"
    aria-label="Next slide"
  >
    <FaChevronRight className="text-red-600 text-sm md:text-base font-bold" />
  </button>
);

export default function SpotlightSlider() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = React.useRef(null);

  // ✅ Slider Settings
  const settings = {
    dots: true,
    infinite: true,
    speed: 800,
    autoplay: isPlaying,
    autoplaySpeed: 4000,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    draggable: true,
    fade: true,
    cssEase: "cubic-bezier(0.645, 0.045, 0.355, 1.0)",
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
    appendDots: dots => (
      <div className="absolute bottom-6 left-0 right-0 z-10">
        <ul className="flex justify-center gap-3 m-0 p-0 list-none bg-transparent">
          {dots}
        </ul>
      </div>
    ),
    customPaging: i => (
      <button
        className={`h-2 rounded-full transition-all duration-300 cursor-pointer focus:outline-none
          ${i === currentSlide 
            ? 'w-8 bg-gradient-to-r from-red-500 to-red-600 shadow-md' 
            : 'w-2 bg-white/60 hover:bg-white/80'}`}
        aria-label={`Go to slide ${i + 1}`}
      />
    ),
  };

  // ✅ Fetch Banners
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await getAllBanners(1);
        setBanners(data || []);
      } catch (err) {
        console.error("Banner API error:", err);
        setError("Banners load nahi ho paaye");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // ✅ Toggle Autoplay
  const toggleAutoplay = () => {
    setIsPlaying(!isPlaying);
  };

  // ✅ Loading State
  if (loading) {
    return (
      <div className="w-full px-0 md:px-0 mt-4">
        <div className="relative overflow-hidden rounded-none md:rounded-2xl shadow-lg">
          <div className="h-[200px] md:h-[350px] lg:h-[450px] w-full bg-gray-200"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-[shimmer_2s_infinite]"></div>
        </div>
        <style>{`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
        `}</style>
      </div>
    );
  }

  // ✅ Error State
  if (error) {
    return (
      <div className="w-full px-4 md:px-8 mt-4">
        <div className="flex flex-col items-center justify-center h-40 bg-red-50 rounded-2xl border border-red-200 shadow-md">
          <p className="text-red-600 text-sm font-medium mb-2">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="text-red-500 text-xs underline hover:no-underline hover:text-red-700 transition-colors"
          >
            Dobara try karo
          </button>
        </div>
      </div>
    );
  }

  // ✅ No Banners
  if (banners.length === 0) return null;

  // ✅ Main Slider
  return (
    <div className="w-full px-0 md:px-0 mt-4">
      <div className="relative rounded-none md:rounded-2xl overflow-hidden shadow-2xl">
        
        <Slider ref={sliderRef} {...settings}>
          {banners.map((item, i) => (
            <div key={i}>
              <div className="relative">
                <img
                  src={`${BANNER_IMG_URL}${item.image}`}
                  alt={`banner-${i}`}
                  className="w-full h-[200px] md:h-[350px] lg:h-[450px] object-cover-fit"
                  loading="lazy"
                />
                
                {/* Slide Counter */}
                <div className="absolute bottom-6 left-6 z-10">
                  <div className="flex items-center gap-2 bg-black/60 backdrop-blur-md text-white text-xs px-3 py-1.5 rounded-full shadow-lg border border-white/20">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
                    <span className="font-medium">{i + 1}</span>
                    <span className="text-white/50">/</span>
                    <span className="font-medium">{banners.length}</span>
                  </div>
                </div>
                
                {/* Title (optional) */}
                {item.title && (
                  <div className="absolute bottom-24 left-6 z-10 max-w-md">
                    <h3 className="text-white text-2xl md:text-3xl font-bold drop-shadow-lg">
                      {item.title}
                    </h3>
                    {item.subtitle && (
                      <p className="text-white/90 text-sm md:text-base mt-1 drop-shadow">
                        {item.subtitle}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </Slider>

        {/* Play/Pause Button */}
        <button
          onClick={toggleAutoplay}
          className="absolute bottom-6 right-6 z-20 
                     w-9 h-9 md:w-11 md:h-11 
                     bg-black/60 backdrop-blur-md 
                     rounded-full flex items-center justify-center
                     text-white 
                     transition-all duration-300
                     shadow-lg
                     border border-white/20
                     hover:bg-black/80 hover:scale-105
                     focus:outline-none focus:ring-2 focus:ring-red-500"
          aria-label={isPlaying ? "Slideshow rok de" : "Slideshow chala de"}
        >
          {isPlaying ? <FaPause size={14} /> : <FaPlay size={14} className="ml-0.5" />}
        </button>
      </div>

      {/* ✅ Only required styles - using Tailwind classes above */}
      <style>{`
        /* Slick Slider Overrides - Only what's needed */
        .slick-slider {
          position: relative;
        }
        
        .slick-list {
          background: transparent !important;
        }
        
        .slick-slide {
          outline: none !important;
          background: transparent !important;
        }
        
        .slick-slide:focus {
          outline: none !important;
        }
        
        .slick-dots {
          background: transparent !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        
        .slick-dots li {
          background: transparent !important;
        }
        
        .slick-dots li button {
          background: transparent !important;
        }
        
        .slick-dots li button:before {
          display: none !important;
        }
        
        /* Animation */
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}